/**
 * IndexedDB via Dexie 4 — database `tik-mail`, schema version 4 (spec §6 + realtime cursors).
 *
 * v1–v3 are the legacy schemas; v4 adds `sync` and `meta` and, on upgrade, clears `messages`
 * and `bodies`: the old bundle mixed a NUL row-key separator with space-separated purges, so
 * cached rows/bodies from it cannot be trusted. Folders, kv, contacts and queued ops survive.
 */
import Dexie, { type Table } from 'dexie';
import type { BodyRec, ContactRec, FolderRec, KvRec, MessageRow, MetaRec, OpRec, SyncRec } from '$lib/types';

export const DB_NAME = 'tik-mail';
export const DB_VERSION = 4;
/** Single row-key separator used everywhere (§21.1). */
export const KEY_SEP = String.fromCharCode(0); // NUL; spelled this way so no tool can mangle the escape
/** `sync` row that carries the SSE `lastEventId`. */
export const SYNC_STREAM_KEY = '*';
/** kv flag for the legacy one-shot purge of search-only rows (spec §1.6). */
export const SEARCH_ROWS_FIX = 'fix.searchrows.v1';

/**
 * True when the error only means "the database handle is gone" — the app is shutting down,
 * the cache was reset, or a test dropped it. Background writers swallow this; it is never a
 * fault the user should see.
 */
export function isDbGone(e: unknown): boolean {
  const name = (e as { name?: string; inner?: { name?: string } })?.name ?? (e as { inner?: { name?: string } })?.inner?.name;
  return name === 'DatabaseClosedError' || name === 'InvalidStateError' || name === 'AbortError';
}

/** Run a background database write, ignoring a closed/aborted handle. */
export async function dbBackground<T>(fn: () => Promise<T>): Promise<T | undefined> {
  try {
    return await fn();
  } catch (e) {
    if (isDbGone(e)) return undefined;
    throw e;
  }
}

const LEGACY_STORES = {
  folders: '&path',
  messages: '&key, folder, [folder+date], date',
  bodies: '&key',
  kv: '&k',
};

export class TikDB extends Dexie {
  folders!: Table<FolderRec, string>;
  messages!: Table<MessageRow, string>;
  bodies!: Table<BodyRec, string>;
  kv!: Table<KvRec, string>;
  ops!: Table<OpRec, number>;
  contacts!: Table<ContactRec, string>;
  sync!: Table<SyncRec, string>;
  meta!: Table<MetaRec, string>;

  constructor(name = DB_NAME) {
    super(name);
    this.version(1).stores(LEGACY_STORES);
    this.version(2).stores({ ...LEGACY_STORES, ops: '++id, ts' });
    this.version(3).stores({ ...LEGACY_STORES, ops: '++id, ts', contacts: '&id, email' });
    this.version(4)
      .stores({
        ...LEGACY_STORES,
        ops: '++id, ts',
        contacts: '&id, email',
        sync: '&folder',
        meta: '&k',
      })
      .upgrade(async (tx) => {
        await tx.table('messages').clear();
        await tx.table('bodies').clear();
        // The legacy one-shot cache purge is satisfied by this upgrade, so record it here.
        await tx.table('kv').put({ k: SEARCH_ROWS_FIX, v: '1' });
      });
    // A database created at v4 has nothing to repair, so nothing else is needed here. (An
    // earlier attempt seeded the flag from `on('populate')`; that event did not fire for a
    // freshly created database in the browser even though fake-indexeddb ran it, so the purge
    // still fired once per install. The upgrade above is the only reliable place.)
  }
}

export let db: TikDB = new TikDB();

/** `folder + "\0" + uid`. */
export function rowKey(folder: string, uid: number): string {
  return folder + KEY_SEP + uid;
}

/** Inverse of `rowKey`; null when the key is malformed. */
export function parseRowKey(key: string | null | undefined): { folder: string; uid: number } | null {
  if (!key) return null;
  const i = key.indexOf(KEY_SEP);
  if (i < 0) return null;
  const uid = Number(key.slice(i + 1));
  if (!Number.isFinite(uid)) return null;
  return { folder: key.slice(0, i), uid };
}

/** Prefix that selects every row/body of a folder (`where('key').startsWith(prefix)`). */
export function folderKeyPrefix(folder: string): string {
  return folder + KEY_SEP;
}

export async function kvGet(k: string): Promise<string | null> {
  return (await db.kv.get(k))?.v ?? null;
}

export async function kvSet(k: string, v: string): Promise<void> {
  await db.kv.put({ k, v });
}

export async function kvDel(k: string): Promise<void> {
  await db.kv.delete(k);
}

export async function metaGet<T = unknown>(k: string): Promise<T | null> {
  return ((await db.meta.get(k))?.v as T | undefined) ?? null;
}

export async function metaSet(k: string, v: unknown): Promise<void> {
  await db.meta.put({ k, v });
}

/** Delete every cached row and body of a folder (used on uidValidity change, empty, resync). */
export async function purgeFolder(folder: string): Promise<void> {
  const prefix = folderKeyPrefix(folder);
  await db.messages.where('folder').equals(folder).delete();
  await db.bodies.where('key').startsWith(prefix).delete();
}

/** Every store, including sync cursors and meta — logout or user change. */
export async function wipeAll(): Promise<void> {
  await Promise.all([
    db.folders.clear(),
    db.messages.clear(),
    db.bodies.clear(),
    db.kv.clear(),
    db.ops.clear(),
    db.contacts.clear(),
    db.sync.clear(),
    db.meta.clear(),
  ]);
}

/** Dexie error names that mean "the on-disk database is unusable, start over". */
const FATAL_OPEN_ERRORS = new Set(['VersionError', 'InvalidStateError', 'UpgradeError', 'DatabaseClosedError', 'MissingAPIError', 'UnknownError', 'OpenFailedError']);

/**
 * Corruption recovery: delete the database, recreate it and record `meta.cacheReset` so the
 * boot path knows the cache is empty for a reason. Safe to call at any time.
 */
export async function resetCache(reason = 'manual'): Promise<void> {
  try {
    db.close();
  } catch {
    /* already closed */
  }
  await Dexie.delete(db.name);
  db = new TikDB(db.name);
  await db.open();
  await metaSet('cacheReset', { at: Date.now(), reason });
}

/**
 * Open the database, falling back to `resetCache()` when Dexie reports a fatal open error.
 * Returns true when a reset happened.
 */
export async function openDb(): Promise<boolean> {
  try {
    if (!db.isOpen()) await db.open();
    return false;
  } catch (err) {
    const name = (err as { name?: string })?.name ?? '';
    if (!FATAL_OPEN_ERRORS.has(name) && !(err instanceof Dexie.DexieError)) throw err;
    await resetCache(name || 'open_failed');
    return true;
  }
}

/** Replace the singleton (tests use a fresh name per run). */
export function useDatabase(instance: TikDB): void {
  db = instance;
}
